<script setup>
/**
 * THE SAVE-TIME WARNING FOR A MISSING LANGUAGE (spec §7 P-7.1 and §2 P-2.10). Both accounts
 * only: the parent passes no rows on a one-language account.
 *
 * It says what the gap costs the customer, lists every field written in one language only,
 * and offers "Translate them for me": ONE batch call per missing language, priced on the
 * button, confirmed in place and charged once (P-5.12). Nothing is written until the owner
 * presses Use this; the parent writes what `apply` hands it. A save is never blocked by any
 * of this: at send time the other language is still shown.
 *
 * Rows come from `helpers/missing-language.js` (`taskMissingWords`, `menuMissingWords`,
 * `handoverMissingWords`), so the warning and every screen that counts a gap agree.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import AiConfirmInline from '../AiConfirmInline.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { useAiConfirm } from '../../composables/useAiConfirm.js';
import { wasloApi, withConfirm } from '../../api/client.js';
import { toIntlLocale } from '../../helpers/intl.js';
import { batchFor, otherLangOf } from '../../helpers/missing-language.js';

const props = defineProps({
  /** `[{ key, kind, what, name, from, to, text, target }]`, both directions. */
  rows: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['apply']);

const { t, locale } = useWasloI18n();
const accountId = useWasloAccountId();
const charge = useAiConfirm({ accountId, op: 'suggest_translations' });
const { receiptText } = charge;

const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

/** One group per missing language, Arabic first: most gaps on a Both account are Arabic. */
const groups = computed(() =>
  ['ar', 'en']
    .map(lang => ({ lang, rows: props.rows.filter(row => row.to === lang) }))
    .filter(group => group.rows.length > 0)
);

/** `{ [row.key]: words }`, proposed and not yet used. */
const proposals = ref({});
/** `{ [row.key]: sentence }` for an item the translator could not give back. */
const notes = ref({});
const working = ref('');
const failure = ref('');

const headlines = group => {
  const options = group.rows.filter(row => row.what === 'option').length;
  const others = group.rows.length - options;
  const lang = group.lang.toUpperCase();
  const lines = [];
  if (options > 0) {
    lines.push(
      t(
        `WASLO.LANGUAGES.OPTIONS_MISSING_${lang}`,
        { count: format.value.format(options) },
        options
      )
    );
  }
  if (others > 0) {
    lines.push(
      t(
        `WASLO.LANGUAGES.OTHERS_MISSING_${lang}`,
        { count: format.value.format(others) },
        others
      )
    );
  }
  return lines;
};

const labelOf = row =>
  t(`WASLO.LANGUAGES.WHAT.${row.what.toUpperCase()}`, { name: row.name });

const translateLabel = group =>
  charge.label(
    t('WASLO.LANGUAGES.TRANSLATE'),
    batchFor(group.rows, group.lang).length
  );

const translate = async group => {
  if (working.value !== '') return;
  const batch = batchFor(group.rows, group.lang);
  if (batch.length === 0) return;
  working.value = group.lang;
  failure.value = '';
  try {
    const outcome = await charge.confirmAndRun({
      count: batch.length,
      run: credits =>
        wasloApi.translateBatch(
          accountId.value,
          {
            from: otherLangOf(group.lang),
            to: group.lang,
            items: batch.map(row => ({ kind: row.kind, text: row.text })),
          },
          undefined,
          withConfirm(credits)
        ),
    });
    if (outcome.cancelled) return;
    const nextProposals = { ...proposals.value };
    const nextNotes = { ...notes.value };
    outcome.result.items.forEach((item, at) => {
      const row = batch[at];
      if (!row) return;
      if (item.outcome === 'ok') {
        nextProposals[row.key] = item.text;
        delete nextNotes[row.key];
        return;
      }
      nextNotes[row.key] =
        item.outcome === 'too_long'
          ? t('WASLO.LANGUAGES.TOO_LONG', {
              length: format.value.format(item.length),
              limit: format.value.format(item.limit),
            })
          : t('WASLO.LANGUAGES.CUT_OFF');
    });
    proposals.value = nextProposals;
    notes.value = nextNotes;
  } catch (error) {
    failure.value =
      error?.status === 402
        ? t('WASLO.TEST.REFUSED.OUT_OF_CREDITS')
        : t('WASLO.LANGUAGES.FAILED');
  } finally {
    working.value = '';
  }
};

const forget = row => {
  const next = { ...proposals.value };
  delete next[row.key];
  proposals.value = next;
};

const accept = row => {
  const text = proposals.value[row.key];
  if (!text) return;
  forget(row);
  emit('apply', { row, text });
};

const acceptAll = group => group.rows.forEach(accept);

const hasProposals = group =>
  group.rows.some(row => Boolean(proposals.value[row.key]));
</script>

<template>
  <section
    v-if="groups.length"
    class="flex flex-col gap-4 p-4 rounded-xl bg-n-alpha-1"
    data-waslo-missing-language
  >
    <div
      v-for="group in groups"
      :key="group.lang"
      class="flex flex-col gap-3"
      :data-waslo-missing-language-group="group.lang"
    >
      <p
        v-for="line in headlines(group)"
        :key="line"
        dir="auto"
        class="mb-0 text-sm font-medium text-n-slate-12"
        data-waslo-missing-language-headline
      >
        {{ line }}
      </p>
      <ul class="flex flex-col gap-2 mb-0">
        <li
          v-for="row in group.rows"
          :key="row.key"
          class="flex flex-col gap-1 text-sm"
          :data-waslo-missing-language-row="row.key"
        >
          <span dir="auto" class="text-n-slate-11">{{ labelOf(row) }}</span>
          <div
            v-if="proposals[row.key]"
            class="flex flex-wrap items-center gap-2"
          >
            <span
              dir="auto"
              class="whitespace-pre-wrap text-n-slate-12"
              data-waslo-missing-language-proposal
            >
              {{ proposals[row.key] }}
            </span>
            <Button
              :label="t('WASLO.TASKS.ARABIC.ACCEPT')"
              size="sm"
              variant="link"
              data-waslo-missing-language-accept
              @click="accept(row)"
            />
            <Button
              :label="t('WASLO.TASKS.ARABIC.DISCARD')"
              size="sm"
              variant="link"
              color="slate"
              data-waslo-missing-language-discard
              @click="forget(row)"
            />
          </div>
          <span
            v-if="notes[row.key]"
            class="text-xs text-n-amber-11"
            data-waslo-missing-language-note
          >
            {{ notes[row.key] }}
          </span>
        </li>
      </ul>
      <div class="flex flex-wrap gap-2">
        <Button
          :label="translateLabel(group)"
          icon="i-lucide-languages"
          size="sm"
          variant="faded"
          color="slate"
          :is-loading="working === group.lang"
          :disabled="disabled || working !== ''"
          data-waslo-missing-language-translate
          @click="translate(group)"
        />
        <Button
          v-if="hasProposals(group)"
          :label="t('WASLO.LANGUAGES.USE_ALL')"
          size="sm"
          data-waslo-missing-language-accept-all
          @click="acceptAll(group)"
        />
      </div>
    </div>
    <AiConfirmInline :controller="charge" />
    <p
      v-if="failure"
      class="mb-0 text-sm text-n-ruby-11"
      data-waslo-missing-language-failure
    >
      {{ failure }}
    </p>
    <p
      v-if="receiptText"
      class="mb-0 text-xs text-n-slate-11"
      data-waslo-ai-receipt
    >
      {{ receiptText }}
    </p>
  </section>
</template>
